import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import posthog from "./lib/analytics";
import { useAuth } from "./context/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import AppShell from "./components/AppShell";
import LoginScreen from "./screens/LoginScreen";
import { useIsMobile } from "./hooks/useIsMobile";

// ── Lazy import with auto-reload on stale chunk (deploy invalidation) ──
function lazyWithReload(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn()
      .then((mod) => {
        // Successful load — clear any pending reload flag
        sessionStorage.removeItem("chunk_reload");
        return mod;
      })
      .catch((err) => {
        // Stale chunk after deploy — reload once
        if (!sessionStorage.getItem("chunk_reload")) {
          sessionStorage.setItem("chunk_reload", "1");
          window.location.reload();
          return new Promise(() => {}); // never resolves — page is reloading
        }
        sessionStorage.removeItem("chunk_reload");
        throw err;
      })
  );
}

// ── Lazy-loaded: marketing / public pages ──
const LandingPageV2 = lazyWithReload(() => import("./screens/LandingPageV2"));
const PricingPage = lazyWithReload(() => import("./screens/PricingPage"));
const PrivacyPage = lazyWithReload(() => import("./screens/PrivacyPage"));
const TermsPage = lazyWithReload(() => import("./screens/TermsPage"));
const BlogIndexScreen = lazyWithReload(() => import("./screens/BlogIndexScreen"));
const BlogPostScreen = lazyWithReload(() => import("./screens/BlogPostScreen"));
const FaqPage = lazyWithReload(() => import("./screens/FaqPage"));
const AboutPage = lazyWithReload(() => import("./screens/AboutPage"));
const ForFamiliesPage = lazyWithReload(() => import("./screens/ForFamiliesPage"));
const BringYourCaregiverPage = lazyWithReload(() => import("./screens/BringYourCaregiverPage"));
const BycAcceptPage = lazyWithReload(() => import("./screens/BycAcceptPage"));
const ForCoordinatorsPage = lazyWithReload(() => import("./screens/ForCoordinatorsPage"));
const VsShiftcarePage = lazyWithReload(() => import("./screens/VsShiftcarePage"));
const VsHomepayPage = lazyWithReload(() => import("./screens/VsHomepayPage"));
const PlatformCaregiversPage = lazyWithReload(() => import("./screens/PlatformCaregiversPage"));
const PlatformFamilyPage = lazyWithReload(() => import("./screens/PlatformFamilyPage"));
const CaregiverDirectoryPage = lazyWithReload(() => import("./screens/CaregiverDirectoryPage"));
const TeamInterestScreen = lazyWithReload(() => import("./screens/TeamInterestScreen"));
const PublicProfilePage = lazyWithReload(() => import("./screens/PublicProfilePage"));
const HandoffPage = lazyWithReload(() => import("./screens/HandoffPage"));
const PublicRateCardScreen = lazyWithReload(() => import("./screens/PublicRateCardScreen"));
const RateGuidePage = lazyWithReload(() => import("./screens/RateGuidePage"));
const NotFoundPage = lazyWithReload(() => import("./screens/NotFoundPage"));
const AccessibilityPage = lazyWithReload(() => import("./screens/AccessibilityPage"));

// ── Lazy-loaded: quick log (standalone, no auth) ──
const QuickLogScreen = lazyWithReload(() => import("./screens/QuickLogScreen"));
const QuickLogImportScreen = lazyWithReload(() => import("./screens/QuickLogImportScreen"));

// ── Lazy-loaded: tools ──
const ToolsIndexScreen = lazyWithReload(() => import("./screens/ToolsIndexScreen"));
const RateCalculatorScreen = lazyWithReload(() => import("./screens/RateCalculatorScreen"));
const RateBuilderScreen = lazyWithReload(() => import("./screens/RateBuilderScreen"));
const FicaCalculatorScreen = lazyWithReload(() => import("./screens/FicaCalculatorScreen"));
const ContractGeneratorScreen = lazyWithReload(() => import("./screens/ContractGeneratorScreen"));
const EmailSignatureGeneratorScreen = lazyWithReload(() => import("./screens/EmailSignatureGeneratorScreen"));

// ── Lazy-loaded: care team ──
const CareTeamAcceptScreen = lazyWithReload(() => import("./screens/CareTeamAcceptScreen"));

// ── Lazy-loaded: family portal ──
const FamilyAcceptInviteScreen = lazyWithReload(() => import("./screens/family/FamilyAcceptInviteScreen"));
const FamilyLoginScreen = lazyWithReload(() => import("./screens/family/FamilyLoginScreen"));
const FamilyForgotPasswordScreen = lazyWithReload(() => import("./screens/family/FamilyForgotPasswordScreen"));
const FamilyResetPasswordScreen = lazyWithReload(() => import("./screens/family/FamilyResetPasswordScreen"));
const FamilyShellEditorial = lazyWithReload(() => import("./components/FamilyShellEditorial"));
const FamilyDashboardEditorial = lazyWithReload(() => import("./screens/family/FamilyDashboardEditorial"));
const FamilySummaryDetailEditorial = lazyWithReload(() => import("./screens/family/FamilySummaryDetailEditorial"));
const FamilyAccountScreenEditorial = lazyWithReload(() => import("./screens/family/FamilyAccountScreenEditorial"));
const FamilyMessagesScreenEditorial = lazyWithReload(() => import("./screens/family/FamilyMessagesScreenEditorial"));
const FamilyScheduleScreenEditorial = lazyWithReload(() => import("./screens/family/FamilyScheduleScreenEditorial"));
const FamilyVitalsScreenEditorial = lazyWithReload(() => import("./screens/family/FamilyVitalsScreenEditorial"));
const FamilyMedicationsScreenEditorial = lazyWithReload(() => import("./screens/family/FamilyMedicationsScreenEditorial"));
const FamilyTimelineScreenEditorial = lazyWithReload(() => import("./screens/family/FamilyTimelineScreenEditorial"));
const FamilyBillingScreenEditorial = lazyWithReload(() => import("./screens/family/FamilyBillingScreenEditorial"));
const FamilyInvoiceDetailEditorial = lazyWithReload(() => import("./screens/family/FamilyInvoiceDetailEditorial"));
const CaregiverProfileViewEditorial = lazyWithReload(() => import("./screens/family/CaregiverProfileViewEditorial"));

// ── Lazy-loaded: core app screens (moved from eager imports) ──
const HomeScreen = lazyWithReload(() => import("./screens/HomeScreen"));
const HomeScreenV5 = lazyWithReload(() => import("./screens/HomeScreenV5"));
// HomeScreenEditorial removed from /dashboard routing: themes are paint, not
// separate dashboards. HomeScreen owns the card grid + customization for
// every theme; visuals diverge via body.theme-* class overrides only.
const ClientListScreenEditorial = lazyWithReload(() => import("./screens/ClientListScreenEditorial"));
const ClientDetailScreenEditorial = lazyWithReload(() => import("./screens/ClientDetailScreenEditorial"));
const ClientTimelineScreenEditorial = lazyWithReload(() => import("./screens/ClientTimelineScreenEditorial"));
const AnnualReportScreen = lazyWithReload(() => import("./screens/AnnualReportScreen"));
const ClientFormScreen = lazyWithReload(() => import("./screens/ClientFormScreen"));
const ShiftLoggingFlowEditorial = lazyWithReload(() => import("./screens/ShiftLoggingFlowEditorial"));
const ActiveShiftScreenEditorial = lazyWithReload(() => import("./screens/ActiveShiftScreenEditorial"));
const ShiftReceiptScreen = lazyWithReload(() => import("./screens/ShiftReceiptScreen"));
const InvoicesScreenEditorial = lazyWithReload(() => import("./screens/InvoicesScreenEditorial"));
const BulkInvoiceReviewScreen = lazyWithReload(() => import("./screens/BulkInvoiceReviewScreen"));
const EarningsScreen = lazyWithReload(() => import("./screens/EarningsScreen"));
const ProfilePageEditorial = lazyWithReload(() => import("./screens/ProfilePageEditorial"));
const AccountScreenEditorial = lazyWithReload(() => import("./screens/AccountScreenEditorial"));
const InvoiceDetailScreen = lazyWithReload(() => import("./screens/InvoiceDetailScreen"));
const ScheduleScreen = lazyWithReload(() => import("./screens/ScheduleScreen"));
const HelpScreen = lazyWithReload(() => import("./screens/HelpScreen"));
const MessagesScreenEditorial = lazyWithReload(() => import("./screens/MessagesScreenEditorial"));

// ── Lazy-loaded: secondary app screens ──
const ProWelcomeScreen = lazyWithReload(() => import("./screens/ProWelcomeScreen"));
const SummaryReviewScreenEditorial = lazyWithReload(() => import("./screens/SummaryReviewScreenEditorial"));
const CareSummariesScreenEditorial = lazyWithReload(() => import("./screens/CareSummariesScreenEditorial"));
const SummariesScreenEditorial = lazyWithReload(() => import("./screens/SummariesScreenEditorial"));
const ReportsScreenEditorial = lazyWithReload(() => import("./screens/ReportsScreenEditorial"));
const MileageScreenEditorial = lazyWithReload(() => import("./screens/MileageScreenEditorial"));
const TaxSummaryScreenEditorial = lazyWithReload(() => import("./screens/TaxSummaryScreenEditorial"));
const TaxesScreenEditorial = lazyWithReload(() => import("./screens/TaxesScreenEditorial"));
const HistoryScreenEditorial = lazyWithReload(() => import("./screens/HistoryScreenEditorial"));
const LeadsScreenEditorial = lazyWithReload(() => import("./screens/LeadsScreenEditorial"));
const AdminScreen = lazyWithReload(() => import("./screens/AdminScreen"));
const AdminRetentionScreen = lazyWithReload(() => import("./screens/AdminRetentionScreen"));
const NotificationsScreenEditorial = lazyWithReload(() => import("./screens/NotificationsScreenEditorial"));
const OnboardingScreen = lazyWithReload(() => import("./screens/OnboardingScreen"));

function RouteTracker() {
  const location = useLocation();
  useEffect(() => { posthog.capture("$pageview"); }, [location.pathname]);
  return null;
}

function RefCapture() {
  useEffect(() => {
    if (localStorage.getItem("carelogic_ref")) return;
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref") || params.get("utm_source");
    if (ref) localStorage.setItem("carelogic_ref", ref);
  }, []);
  return null;
}

/** Apply saved theme overrides before first paint to prevent flash */
function ThemeRestorer() {
  useEffect(() => {
    try {
      const saved = localStorage.getItem("carelogic_theme_overrides");
      if (saved) {
        const overrides = JSON.parse(saved);
        for (const [key, value] of Object.entries(overrides)) {
          document.documentElement.style.setProperty(key, value as string);
        }
        // Auto-adjust logo color for dark sidebar
        const sidebarBg = overrides["--color-sidebar-bg"];
        if (sidebarBg) {
          const c = sidebarBg.replace("#", "");
          const lum = (parseInt(c.substring(0, 2), 16) * 299 + parseInt(c.substring(2, 4), 16) * 587 + parseInt(c.substring(4, 6), 16) * 114) / 1000;
          if (lum < 128) {
            document.documentElement.style.setProperty("--color-logo-care", "#FFFFFF");
            document.documentElement.style.setProperty("--color-sidebar-text", "#E7E9EA");
          }
        }
      }
    } catch {
      // ignore malformed data
    }
    // Restore flush layout toggles
    if (localStorage.getItem("carelogic_flush_center") === "true") document.documentElement.classList.add("flush-center");
    if (localStorage.getItem("carelogic_flush_left") === "true") document.documentElement.classList.add("flush-left");
    if (localStorage.getItem("carelogic_flush_right") === "true") document.documentElement.classList.add("flush-right");
  }, []);
  return null;
}

/** Keeps body.theme-* in sync with the active dashboard view — at the App
 *  level, so the theme survives route changes (sidebar would otherwise revert
 *  to default tokens when HomeScreen unmounts). */
function ViewThemeTracker() {
  useEffect(() => {
    const THEME_CLASSES = ["theme-concierge", "theme-shift-report"];
    const apply = () => {
      try {
        // Global preset (set from ThemeEditor) wins app-wide.
        //   "classic" → no theme class (reset to default tokens)
        //   "concierge" / "shift_report" → apply matching class
        //   unset → fall back to the active dashboard view's theme
        const globalPref = localStorage.getItem("carelogic_global_theme");
        let themeId: string | null = globalPref;
        if (!globalPref) {
          const activeId = localStorage.getItem("carelogic_active_view_id");
          const cacheRaw = localStorage.getItem("carelogic_active_view_id_views_cache");
          if (activeId && cacheRaw) {
            const cache = JSON.parse(cacheRaw) as Array<{ id: string; theme: string | null }>;
            themeId = cache.find(v => v.id === activeId)?.theme ?? null;
          }
        }
        const targetClass =
          themeId === "concierge" ? "theme-concierge" :
          themeId === "shift_report" ? "theme-shift-report" :
          null;
        for (const c of THEME_CLASSES) {
          if (c !== targetClass) document.body.classList.remove(c);
        }
        if (targetClass) document.body.classList.add(targetClass);
      } catch { /* ignore malformed cache */ }
    };
    apply();
    window.addEventListener("storage", apply);
    window.addEventListener("carelogic:view-changed", apply);
    return () => {
      window.removeEventListener("storage", apply);
      window.removeEventListener("carelogic:view-changed", apply);
    };
  }, []);
  return null;
}

function DashboardPage() {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", color: "var(--color-text-muted)" }}>Loading...</div>;
  if (!user) return <LoginScreen />;
  if (sessionId) return <Suspense fallback={<LoadingFallback />}><ProWelcomeScreen sessionId={sessionId} /></Suspense>;
  // V5 dashboard opt-in: ?v5=0 hard-disables, ?v5=1 hard-enables, otherwise localStorage["dashboard_version"]
  const v5Param = searchParams.get("v5");
  const v5Persistent = typeof window !== "undefined" && localStorage.getItem("dashboard_version") === "v5";
  const useV5 = v5Param === "1" || (v5Param !== "0" && v5Persistent);
  if (useV5) return <Suspense fallback={<LoadingFallback />}><HomeScreenV5 /></Suspense>;
  return <Suspense fallback={<LoadingFallback />}><HomeScreen /></Suspense>;
}

// /clients/:id — on desktop (≥1024px) render the master/detail split (the list
// screen reads :id as the selected client); on mobile render the plain full-
// screen detail exactly as before. Deep links work in both cases.
function ClientDetailRoute() {
  const isDesktop = !useIsMobile(1024);
  return (
    <Suspense fallback={<LoadingFallback />}>
      {isDesktop ? <ClientListScreenEditorial /> : <ClientDetailScreenEditorial />}
    </Suspense>
  );
}

// Silent placeholder during route-level code-split fetch. Was a centered
// "Loading..." text which read as a hard loading state for ~1–2s on first
// visit to marketing pages. Now a cream surface that matches the marketing
// background, so the transition is from blank cream -> rendered page
// chrome (no jarring text swap). For authenticated routes the surface
// reads as the app shell background.
// Lazy-route Suspense fallback. Was a blank div — visible as a "blank
// flash" between route navigation and the lazy chunk arriving (2026-05-28
// UI audit, ISSUE-216 surfaced on /profile but applied to every route).
// Now renders a V5-shaped skeleton: navy banner placeholder + three
// content blocks. Generic enough to read as "loading this page" on any
// route without faking specific layouts.
function LoadingFallback() {
  return (
    <div
      aria-hidden
      style={{
        height: "100%",
        minHeight: "60vh",
        background: "var(--color-bg, #F5EFE6)",
        paddingBottom: 24,
      }}
    >
      {/* Navy banner placeholder, matches V5ScreenHeader Classic dimensions. */}
      <div style={{ padding: "14px 20px 8px" }}>
        <div
          style={{
            background: "var(--color-banner, #1B2A4A)",
            borderRadius: 16,
            height: 72,
            opacity: 0.6,
          }}
        />
      </div>
      {/* Content card skeletons. */}
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ padding: "12px 20px 0" }}>
          <div
            style={{
              background: "var(--color-surface, #FFFFFF)",
              border: "1px solid var(--color-border, #E5DDD0)",
              borderRadius: 14,
              height: i === 0 ? 120 : 88,
              animation: "carelogic-skeleton-pulse 1.4s ease-in-out infinite",
              animationDelay: `${i * 0.12}s`,
            }}
          />
        </div>
      ))}
      <style>{`
        @keyframes carelogic-skeleton-pulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 0.85; }
        }
      `}</style>
    </div>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <RouteTracker />
      <RefCapture />
      <ThemeRestorer />
      <ViewThemeTracker />
      <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/admin" element={<AdminScreen />} />
        <Route path="/admin/retention" element={<AdminRetentionScreen />} />
        <Route path="/login" element={<LoginScreen />} />
        <Route path="/onboarding" element={<OnboardingScreen />} />
        <Route path="/log" element={<QuickLogScreen />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/blog" element={<BlogIndexScreen />} />
        <Route path="/blog/:slug" element={<BlogPostScreen />} />
        <Route path="/" element={<LandingPageV2 />} />
        <Route path="/team-interest" element={<TeamInterestScreen />} />
        <Route path="/for-coordinators" element={<ForCoordinatorsPage />} />
        <Route path="/vs/shiftcare" element={<VsShiftcarePage />} />
        <Route path="/vs/homepay" element={<VsHomepayPage />} />
        <Route path="/tools" element={<ToolsIndexScreen />} />
        <Route path="/tools/rate-calculator" element={<RateCalculatorScreen />} />
        <Route path="/tools/rate-builder" element={<RateBuilderScreen />} />
        <Route path="/tools/fica-calculator" element={<FicaCalculatorScreen />} />
        <Route path="/tools/contract-generator" element={<ContractGeneratorScreen />} />
        <Route path="/tools/signature-generator" element={<EmailSignatureGeneratorScreen />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/c/:slug" element={<PublicProfilePage />} />
        <Route path="/handoff/:token" element={<HandoffPage />} />
        <Route path="/rates/:slug" element={<PublicRateCardScreen />} />
        <Route path="/guides/caregiver-rates/:state" element={<RateGuidePage />} />
        <Route path="/guides/caregiver-rates" element={<RateGuidePage />} />
        <Route path="/for-families" element={<ForFamiliesPage />} />
        <Route path="/for-families/bring-your-caregiver" element={<BringYourCaregiverPage />} />
        <Route path="/byc/accept/:token" element={<BycAcceptPage />} />
        <Route path="/platform/caregivers" element={<PlatformCaregiversPage />} />
        <Route path="/caregivers" element={<CaregiverDirectoryPage />} />
        <Route path="/platform/family" element={<PlatformFamilyPage />} />
        {/* Care Team — accept invite */}
        <Route path="/care-team/accept" element={<CareTeamAcceptScreen />} />
        {/* Family Portal — public auth routes */}
        <Route path="/family" element={<Navigate to="/family/login" replace />} />
        <Route path="/family/accept" element={<FamilyAcceptInviteScreen />} />
        <Route path="/family/login" element={<FamilyLoginScreen />} />
        <Route path="/family/forgot-password" element={<FamilyForgotPasswordScreen />} />
        <Route path="/family/reset-password" element={<FamilyResetPasswordScreen />} />
        {/* Family Portal — authenticated routes */}
        <Route element={<FamilyShellEditorial />}>
          <Route path="/family/dashboard" element={<FamilyDashboardEditorial />} />
          <Route path="/family/timeline" element={<FamilyTimelineScreenEditorial />} />
          <Route path="/family/messages" element={<FamilyMessagesScreenEditorial />} />
          <Route path="/family/schedule" element={<FamilyScheduleScreenEditorial />} />
          <Route path="/family/vitals" element={<FamilyVitalsScreenEditorial />} />
          <Route path="/family/medications" element={<FamilyMedicationsScreenEditorial />} />
          <Route path="/family/billing" element={<FamilyBillingScreenEditorial />} />
          <Route path="/family/invoice/:invoiceId" element={<FamilyInvoiceDetailEditorial />} />
          <Route path="/family/summary/:id" element={<FamilySummaryDetailEditorial />} />
          <Route path="/family/account" element={<FamilyAccountScreenEditorial />} />
          <Route path="/family/caregiver" element={<CaregiverProfileViewEditorial />} />
        </Route>
        <Route element={<AppShell />}>
          <Route path="/log/import" element={<QuickLogImportScreen />} />
          <Route path="/notifications" element={<NotificationsScreenEditorial />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/clients" element={<ClientListScreenEditorial />} />
          <Route path="/clients/new" element={<ClientFormScreen />} />
          <Route path="/clients/:id" element={<ClientDetailRoute />} />
          <Route path="/clients/:id/timeline" element={<ClientTimelineScreenEditorial />} />
          <Route path="/clients/:id/annual-report" element={<AnnualReportScreen />} />
          <Route path="/clients/:id/edit" element={<ClientFormScreen />} />
          <Route path="/clients/:id/shift" element={<ShiftLoggingFlowEditorial />} />
          <Route path="/shifts/:id/active" element={<ActiveShiftScreenEditorial />} />
          <Route path="/shifts/:id/receipt" element={<ShiftReceiptScreen />} />
          <Route path="/shifts/:id/summary" element={<SummaryReviewScreenEditorial />} />
          <Route path="/invoices" element={<InvoicesScreenEditorial />} />
          <Route path="/invoices/new/bulk" element={<BulkInvoiceReviewScreen />} />
          {/* /invoices/new used to fall through to /invoices/:id with
              id="new" and render "Invoice not found". Redirect to the
              bulk-review flow which has a sensible empty state when
              there's nothing to invoice. (Fix from 2026-05-20 audit P0 #1.) */}
          <Route path="/invoices/new" element={<Navigate to="/invoices/new/bulk" replace />} />
          <Route path="/invoices/:id" element={<InvoiceDetailScreen />} />
          <Route path="/account" element={<AccountScreenEditorial />} />
          <Route path="/care-summaries" element={<CareSummariesScreenEditorial />} />
          <Route path="/summaries" element={<SummariesScreenEditorial />} />
          <Route path="/my-business" element={<EarningsScreen />} />
          <Route path="/help" element={<HelpScreen />} />
          {/* /business is the URL guessed from the sidebar label
              "Your business" — redirect to canonical /my-business. */}
          <Route path="/business" element={<Navigate to="/my-business" replace />} />
          <Route path="/earnings" element={<Navigate to="/my-business" replace />} />
          <Route path="/reports" element={<ReportsScreenEditorial />} />
          <Route path="/mileage" element={<MileageScreenEditorial />} />
          <Route path="/tax-summary" element={<TaxSummaryScreenEditorial />} />
          <Route path="/taxes" element={<TaxesScreenEditorial />} />
          <Route path="/history" element={<HistoryScreenEditorial />} />
          <Route path="/messages" element={<MessagesScreenEditorial />} />
          <Route path="/schedule" element={<ScheduleScreen />} />
          <Route path="/leads" element={<LeadsScreenEditorial />} />
          <Route path="/profile" element={<ProfilePageEditorial />} />
          <Route path="/family-portal" element={<Navigate to="/family/login" replace />} />
        </Route>
        <Route path="/accessibility" element={<AccessibilityPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
    </ErrorBoundary>
  );
}
